import { MdOutlineReply } from "react-icons/md";

import { type SxProps, type Theme } from "@mui/material/styles";

import { Avatar } from "ui/avatar";
import { Box } from "ui/box";
import { Stack } from "ui/stack";
import { mergeSx } from "ui/merge-sx";
import { CommentableHeader } from "containers/admin/clients/touchpoint/components/comments/commentable-header/commentable-header";

export interface EmailHeaders {
  senderName: string;
  senderEmail: string;
  replyToEmail: string;
  subjectLine?: string;
  preheader?: string;
  id?: string;
}

export const initialEmailHeaders: EmailHeaders = {
  senderName: "Not defined",
  senderEmail: "Not defined",
  replyToEmail: "Not defined",
  subjectLine: "[Email subject line]",
  preheader: "[Email preheader text...]",
};

interface EmailHeadersPreviewProps {
  emailHeaders: EmailHeaders;
  isCommentMode: boolean;
  isMobileView?: boolean;
  /** When true, subject line and preheader are omitted (e.g. program templates). */
  hideSubjectAndPreheader?: boolean;
  className?: string;
  sx?: SxProps<Theme>;
}
export const EmailHeaderPreview = ({
  emailHeaders,
  isCommentMode,
  isMobileView,
  hideSubjectAndPreheader = false,
  className,
  sx,
}: EmailHeadersPreviewProps) => {
  const headerDetails: EmailHeaders = {
    senderName: emailHeaders.senderName,
    replyToEmail: emailHeaders.replyToEmail,
    subjectLine: emailHeaders.subjectLine,
    preheader: emailHeaders.preheader,
    senderEmail: emailHeaders.senderEmail,
  };

  const avatarInitial = headerDetails.senderName.charAt(0);
  const avatarFill =
    avatarInitial === headerDetails.senderName.charAt(0) ? undefined : avatarInitial;

  /** Chakra spacing units (0.25rem step) between the header fields. */
  const getHeaderSpacing = () => {
    const headerSpacing = isMobileView ? 1 : 2;
    return isCommentMode ? headerSpacing * 2 : headerSpacing;
  };

  return (
    <Box
      data-testid="email-builder__preview-header"
      className={className}
      sx={mergeSx({ display: "flex", px: "1rem", py: "0.75rem" }, sx)}
    >
      <Avatar name={avatarFill} sx={{ mr: isCommentMode ? 2 : 0 }} />
      <Stack
        spacing={0}
        sx={{ pl: "0.75rem", flex: 1 }}
        className={`text-gray-600 text-sm ${isCommentMode ? "space-y-2" : "space-y-1.5"}`}
      >
        <Stack
          direction={isMobileView ? "column" : "row"}
          spacing={`${getHeaderSpacing() * 0.25}rem`}
          sx={{ "& > div:last-child": { ml: isMobileView ? 0 : "auto" } }}
        >
          <CommentableHeader
            moduleId="senderName"
            isCommentMode={isCommentMode}
            sx={{ mr: isCommentMode ? 2 : 0 }}
          >
            <span className="font-bold">{headerDetails.senderName}</span>
          </CommentableHeader>

          <CommentableHeader
            moduleId="senderEmail"
            isCommentMode={isCommentMode}
            sx={{ ml: isCommentMode ? 3 : 0 }}
          >
            <span>&lt;{headerDetails.senderEmail}&gt;</span>
          </CommentableHeader>

          <CommentableHeader moduleId="replyToEmail" isCommentMode={isCommentMode}>
            <Box sx={{ display: "flex" }}>
              <Box
                component={MdOutlineReply}
                sx={{ width: "18px", height: "20px", mr: "3px", flexShrink: 0 }}
              />
              <span>&lt;{headerDetails.replyToEmail}&gt;</span>
            </Box>
          </CommentableHeader>
        </Stack>

        {!hideSubjectAndPreheader && (
          <>
            <CommentableHeader moduleId="subjectLine" isCommentMode={isCommentMode}>
              <span className="font-medium">{headerDetails.subjectLine}</span>
            </CommentableHeader>

            <CommentableHeader moduleId="preheader" isCommentMode={isCommentMode}>
              <span className="text-xs text-gray-500">{headerDetails.preheader}</span>
            </CommentableHeader>
          </>
        )}
      </Stack>
    </Box>
  );
};
